import * as React from "react";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import {
  Building2,
  Bell,
  PlusCircle,
  CheckCircle2,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Search,
} from "lucide-react";
import OrgDetailPanel from "./OrgDetailPanel";
import {
  IOrgBreakdown,
  IOrgDetailData,
  IOrgAggregates,
  IDateRange,
  DateRangePreset,
} from "../analytics.types";
import DateRangeFilter from "./DateRangeFilter";

interface IOrganizationOverviewProps {
  sortedOrgs: IOrgBreakdown[];
  orgSortField: keyof IOrgBreakdown;
  orgSortDirection: "asc" | "desc";
  onSort: (field: keyof IOrgBreakdown) => void;
  expandedOrgId: string | null;
  onOrgExpand: (orgId: string) => void;
  expandedOrgDetail: IOrgDetailData | null;
  orgAggregates: IOrgAggregates;
  dateRange: IDateRange;
  dateRangePresets: { key: DateRangePreset; label: string }[];
  onPresetChange: (preset: DateRangePreset) => void;
  onApplyCustomDateRange: (startDate: string, endDate: string) => void;
}

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_ITEMS_PER_PAGE = 10;

const SortIndicator = ({
  field,
  activeField,
  direction,
}: {
  field: keyof IOrgBreakdown;
  activeField: keyof IOrgBreakdown;
  direction: "asc" | "desc";
}) => {
  if (activeField !== field) {
    return <ArrowUpDown size={14} className="sort-icon" />;
  }

  return direction === "desc" ? (
    <ChevronDown size={14} className="sort-icon active" />
  ) : (
    <ChevronUp size={14} className="sort-icon active" />
  );
};

const SORTABLE_COLUMNS: {
  field: keyof IOrgBreakdown;
  label: string;
  align?: string;
}[] = [
  { field: "totalDevices", label: "Devices", align: "text-center" },
  { field: "onlineDevices", label: "Online", align: "text-center" },
  { field: "alertsSent", label: "Alerts Sent", align: "text-center" },
  { field: "devicesAdded", label: "Devices Added", align: "text-center" },
  { field: "alertSuccessRate", label: "Success Rate", align: "text-center" },
];

const canExpandRow = (organization: IOrgBreakdown) =>
  organization.alertsSent > 0 || organization.devicesAdded > 0;

const OrganizationOverview = ({
  sortedOrgs,
  orgSortField,
  orgSortDirection,
  onSort,
  expandedOrgId,
  onOrgExpand,
  expandedOrgDetail,
  orgAggregates,
  dateRange,
  dateRangePresets,
  onPresetChange,
  onApplyCustomDateRange,
}: IOrganizationOverviewProps): React.JSX.Element => {

  const [organizationSearchTerm, setOrganizationSearchTerm] =
    React.useState<string>("");
  const [currentPageIndex, setCurrentPageIndex] = React.useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = React.useState<number>(
    DEFAULT_ITEMS_PER_PAGE,
  );

  const validOrganizations: IOrgBreakdown[] = React.useMemo(() => {
    return sortedOrgs.filter((organization: IOrgBreakdown) => {
      const organizationName = String(organization.orgName || "").trim();
      return Boolean(organizationName);
    });
  }, [sortedOrgs]);

  const searchedOrganizations: IOrgBreakdown[] = React.useMemo(() => {
    const normalizedSearchTerm = organizationSearchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return validOrganizations;
    }

    return validOrganizations.filter((organization: IOrgBreakdown) =>
      String(organization.orgName || "")
        .toLowerCase()
        .includes(normalizedSearchTerm),
    );
  }, [validOrganizations, organizationSearchTerm]);

  const totalPages: number = Math.max(
    1,
    Math.ceil(searchedOrganizations.length / itemsPerPage),
  );

  const paginatedOrganizations: IOrgBreakdown[] = React.useMemo(() => {
    const startIndex = currentPageIndex * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return searchedOrganizations.slice(startIndex, endIndex);
  }, [searchedOrganizations, currentPageIndex, itemsPerPage]);

  const showEmptyState: boolean = validOrganizations.length === 0;
  const showNoSearchResultsState: boolean =
    validOrganizations.length > 0 && searchedOrganizations.length === 0;

  React.useEffect(() => {
    setCurrentPageIndex(0);
  }, [organizationSearchTerm, itemsPerPage]);

  React.useEffect(() => {
    const lastValidPageIndex = Math.max(totalPages - 1, 0);

    if (currentPageIndex > lastValidPageIndex) {
      setCurrentPageIndex(lastValidPageIndex);
    }
  }, [currentPageIndex, totalPages]);

  return (
    <div className="analytics-card org-table-card">
      <div className="card-title">
        <Building2 size={20} strokeWidth={1.5} />
        <h2>Organization Overview</h2>
        <span className="badge-total">{dateRange.label}</span>
      </div>

      <div className="analytics-section">
        <DateRangeFilter
          dateRange={dateRange}
          dateRangePresets={dateRangePresets}
          onPresetChange={onPresetChange}             
          onApplyCustomDateRange={onApplyCustomDateRange}
        />
      </div>

      <div className="org-summary-header">
        <div className="org-summary-strip">
          <div className="summary-stat">
            <Bell size={16} strokeWidth={1.5} />
            <span className="stat-value">
              {orgAggregates.totalAlertsSent.toLocaleString()}
            </span>
            <span className="stat-label">Alerts Sent</span>
          </div>

          <div className="summary-stat">
            <PlusCircle size={16} strokeWidth={1.5} />
            <span className="stat-value">
              {orgAggregates.totalDevicesAdded.toLocaleString()}
            </span>
            <span className="stat-label">Devices Added</span>
          </div>

          <div className="summary-stat">
            <CheckCircle2 size={16} strokeWidth={1.5} />
            <span className="stat-value">{orgAggregates.avgSuccessRate}%</span>
            <span className="stat-label">Avg Success Rate</span>
          </div>
        </div>

        <div className="org-table-toolbar">
          <div className="org-search-box">
            <Search size={16} className="org-search-icon" />
            <input
              type="text"
              value={organizationSearchTerm}
              onChange={(event) =>
                setOrganizationSearchTerm(event.target.value)
              }
              placeholder="Search by org name"
              aria-label="Search organizations by name"
              className="org-search-input"
            />
          </div>
        </div>
      </div>

      {showEmptyState ? (
        <div className="analytics-empty-state">
          No organization data available.
        </div>
      ) : showNoSearchResultsState ? (
        <div className="analytics-empty-state">No records found.</div>
      ) : (
        <>
          <div className="org-table-wrapper">
            <table className="org-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>

                  <th onClick={() => onSort("orgName")} className="sortable">
                    Organization{" "}
                    <SortIndicator
                      field="orgName"
                      activeField={orgSortField}
                      direction={orgSortDirection}
                    />
                  </th>

                  {SORTABLE_COLUMNS.map((column) => (
                    <th
                      key={column.field}
                      onClick={() => onSort(column.field)}
                      className={`sortable ${column.align || ""}`}
                    >
                      {column.label}{" "}
                      <SortIndicator
                        field={column.field}
                        activeField={orgSortField}
                        direction={orgSortDirection}
                      />
                    </th>
                  ))}

                  <th className="text-center">Top Source</th>

                  <th
                    onClick={() => onSort("totalUsers")}
                    className="sortable text-center"
                  >
                    Users{" "}
                    <SortIndicator
                      field="totalUsers"
                      activeField={orgSortField}
                      direction={orgSortDirection}
                    />
                  </th>

                  <th className="text-center">Health</th>
                </tr>
              </thead>

              <tbody>
                {paginatedOrganizations.map((org) => {
                  const isExpanded = expandedOrgId === org.orgId;
                  const isExpandable = canExpandRow(org);

                  return (
                    <React.Fragment key={org.orgId || org.orgName}>
                      <OrgRow
                        org={org}
                        isExpanded={isExpanded}
                        isExpandable={isExpandable}
                        onExpand={() => onOrgExpand(org.orgId)}
                      />

                      {isExpanded && isExpandable && expandedOrgDetail && (
                        <tr className="org-detail-row">
                          <td colSpan={10}>
                            <OrgDetailPanel
                              org={org}
                              detail={expandedOrgDetail}
                              dateLabel={dateRange.label}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pagination-container">
            <div className="pagination-content">
              <div className="dm-page-input">
                <span>Item per page</span>
                <Form.Select
                  aria-label="Organization overview page size"
                  value={itemsPerPage}
                  onChange={(event) => {
                    setItemsPerPage(Number(event.target.value));
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((pageSizeOption) => (
                    <option key={pageSizeOption} value={pageSizeOption}>
                      {pageSizeOption}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="dm-page-items">
                <span className="page-indicator">
                  <div>Page</div>
                  <strong>
                    {currentPageIndex + 1} of {totalPages}
                  </strong>
                </span>
              </div>
            </div>

            <div className="dm-pagination">
              <button
                onClick={() =>
                  setCurrentPageIndex((previousPageIndex) =>
                    Math.max(previousPageIndex - 1, 0),
                  )
                }
                disabled={currentPageIndex === 0}
              >
                <ChevronLeft size={16} />
              </button>

              <button
                onClick={() =>
                  setCurrentPageIndex((previousPageIndex) =>
                    Math.min(previousPageIndex + 1, totalPages - 1),
                  )
                }
                disabled={currentPageIndex >= totalPages - 1}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const OrgRow = ({
  org,
  isExpanded,
  isExpandable,
  onExpand,
}: {
  org: IOrgBreakdown;
  isExpanded: boolean;
  isExpandable: boolean;
  onExpand: () => void;
}) => {
  const healthPct =
    org.totalDevices > 0
      ? Math.round((org.onlineDevices / org.totalDevices) * 100)
      : 0;

  const handleRowClick = () => {
    if (isExpandable) {
      onExpand();
    }
  };

  const expandIcon = (
    <span className="expand-icon-wrapper">
      <ChevronRight
        size={16}
        className={`expand-icon ${isExpanded ? "rotated" : ""} ${!isExpandable ? "disabled" : ""}`}
      />
    </span>
  );

  return (
    <tr
      className={`org-row ${isExpanded ? "expanded" : ""} ${!isExpandable ? "disabled-expand" : ""}`}
      onClick={handleRowClick}
    >
      <td className="expand-cell">
        {isExpandable ? (
          expandIcon
        ) : (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`expand-disabled-${org.orgId}`}>
                No detail available for this organization
              </Tooltip>
            }
          >
            {expandIcon}
          </OverlayTrigger>
        )}
      </td>

      <td>
        <div className="org-name-cell">
          <span className={`status-indicator ${org.status.toLowerCase()}`} />
          {org.orgName}
        </div>
      </td>

      <td className="text-center">{org.totalDevices}</td>

      <td className="text-center">
        <span className="online-count">{org.onlineDevices}</span>
      </td>

      <td className="text-center">
        <span className="alert-sent-count">{org.alertsSent}</span>
      </td>

      <td className="text-center">
        <span className="devices-added-count">+{org.devicesAdded}</span>
      </td>

      <td className="text-center">
        <span
          className={`success-rate ${
            org.alertSuccessRate >= 95
              ? "high"
              : org.alertSuccessRate >= 85
                ? "medium"
                : "low"
          }`}
        >
          {org.alertSuccessRate}%
        </span>
      </td>

      <td className="text-center">
        <span className="source-tag">{org.topAlertSource}</span>
      </td>

      <td className="text-center">{org.totalUsers}</td>

      <td className="text-center">
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`health-${org.orgId}`}>
              {healthPct}% devices online
            </Tooltip>
          }
        >
          <div className="health-bar-container">
            <div
              className="health-bar"
              style={{
                width: `${healthPct}%`,
                backgroundColor:
                  healthPct > 90
                    ? "var(--fuji-severity-low)"
                    : healthPct > 70
                      ? "var(--fuji-severity-medium)"
                      : "var(--fuji-severity-high)",
              }}
            />
          </div>
        </OverlayTrigger>
      </td>
    </tr>
  );
};

export default OrganizationOverview;
